import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { IonPage, useIonViewDidEnter, useIonViewWillEnter } from '@ionic/react';
import {
  AppFooter_v2 as AppFooter,
  Button_v2 as Button,
  Container_v2 as Container,
  ScreenInput,
  Typography,
  TypographyVariantsEnum,
} from '@mindly/ui-components';
import { useTranslation } from 'react-i18next';
import { PrivateRoutes, PublicRoutes } from '../../../routes/navigation';
import { useAnalytics, useAuthFlow, useIsKeyBoardShown, validateEmail } from '../../../shared';
import { RouteComponentProps } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthAction, setSignUpEmail } from '../../../redux/auth/reducer';
import { signUpEmailSelector } from '../../../redux/auth/selector';
import { AuthPageHeaderFeature } from 'src/features/AuthPageHeaderFeature';
import useToast from 'src/context/ToastContext/ToastContext';

const EmailSignInPage: FC<RouteComponentProps> = ({ history }) => {
  const { t } = useTranslation('auth');
  const { triggerSignInWithCustomTokenFlow, isEmailExist, authUser } = useAuthFlow();
  const dispatch = useDispatch();
  const { trackPredefinedEvent } = useAnalytics();
  const isKeyboardShown = useIsKeyBoardShown();

  const emailRef = useRef<HTMLInputElement | null>(null);

  const email = useSelector(signUpEmailSelector);

  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const { showToast } = useToast();

  useIonViewWillEnter(() => {
    const token = localStorage.getItem('onboardingToken');

    if (authUser && !authUser?.isAnonymous && authUser?.email && !token) {
      history.push(PrivateRoutes.PSYCHOLOGIST_LIST);
    }
  }, [authUser]);

  useIonViewDidEnter(() => {
    trackPredefinedEvent('SIGNUP_USER_INFO_VIEWED', {
      screen_type: 'email',
    });
  });

  useIonViewDidEnter(() => {
    if (emailRef.current) {
      emailRef.current.focus();
    }
  }, [emailRef]);

  useEffect(() => {
    setIsDisabled(!validateEmail(email));
  }, [email]);

  const handleEmailSubmit = useCallback(async () => {
    if (isDisabled) return;

    try {
      setIsLoading(true);

      if (authUser) {
        const token = localStorage.getItem('onboardingToken');
        if (token) {
          await triggerSignInWithCustomTokenFlow(token);
        }
      }

      const isEmailExistResult = await isEmailExist(email);

      if (typeof isEmailExistResult !== 'boolean') {
        return;
      }

      dispatch(setAuthAction(isEmailExistResult ? 'signIn' : authUser ? 'link' : 'signUp'));

      trackPredefinedEvent('SIGNUP_USER_INFO_SUBMITTED', {
        screen_type: 'email',
        value: email,
        is_login: isEmailExistResult,
      });

      if (isEmailExistResult) {
        history.push(PublicRoutes.PASSWORD);
      } else {
        history.push(PublicRoutes.NAME_SIGN_UP);
      }
    } catch (error) {
      showToast({ error });
    } finally {
      setIsLoading(false);
    }
  }, [email, authUser, isDisabled]);

  return (
    <IonPage>
      <AuthPageHeaderFeature backButtonLink={PublicRoutes.AUTH} />
      <Container isFlexContainer>
        <div className={'flex flex-col gap-1'}>
          <Typography variant={TypographyVariantsEnum.Title28Semi}>{t('auth.enter.email.title')}</Typography>
        </div>
        <ScreenInput
          ref={emailRef}
          type={'email'}
          name={'email'}
          placeholder={'your.email@address.com'}
          value={email}
          autoComplete={'email'}
          className={'px-5'}
          onChange={(e) => dispatch(setSignUpEmail(e.target.value))}
          onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
          required
        />
      </Container>
      <AppFooter transparent bottomSaveArea={!isKeyboardShown}>
        <Button
          isLoading={isLoading}
          disabled={isDisabled}
          fullWidth
          size={'large'}
          onClick={() => handleEmailSubmit()}
        >
          {t('auth.button.next')}
        </Button>
      </AppFooter>
    </IonPage>
  );
};

export default EmailSignInPage;
